import Badge from 'components/common/Badge'
import { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import { TicketComment } from './types'
import { formatTicketDate, getTicketCommentTypeLabel, ticketCommentTypeBadgeVariants } from './util'
import FileIcon from '@ui-components-3/icons/lib/regular/FileLines'

type MemberTicketCommentViewProps = {
  comment: TicketComment
}

const MemberTicketCommentView = ({ comment }: MemberTicketCommentViewProps) => {
  const { author, externalCreatedAt, type, body, attachments, public: isPublic } = comment || {}

  const formattedDate = useMemo(() => formatTicketDate(externalCreatedAt), [externalCreatedAt])
  const commentType = isPublic ? 'external' : 'internal'

  const attachmentsCount = attachments?.length

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-neutral-300 py-4 text-neutral-800">
      <div className="flex flex-row flex-wrap items-start justify-between gap-2 px-4">
        <div className="flex shrink-0 flex-col gap-1">
          <p>
            <b className="mr-2 font-semibold">Added By:</b>
            {author}
          </p>
          <p className="whitespace-pre">
            <b className="mr-2 font-semibold">Date:</b>
            {formattedDate}
          </p>
        </div>
        <Badge variant={ticketCommentTypeBadgeVariants[commentType]}>{getTicketCommentTypeLabel(commentType)}</Badge>
      </div>
      <hr />
      <div className="px-4">
        <ReactMarkdown
          components={{
            p: ({ children }) => <p className="typography-body">{children}</p>,
            h1: ({ children }) => <h1 className="typography-h1">{children}</h1>,
            h2: ({ children }) => <h2 className="typography-h2">{children}</h2>,
            h3: ({ children }) => <h3 className="typography-h3">{children}</h3>,
            h4: ({ children }) => <h4 className="typography-h4">{children}</h4>,
            ul: ({ children }) => <ul className="typography-body list-disc pl-4">{children}</ul>,
            ol: ({ children }) => <ol className="typography-body list-decimal pl-4">{children}</ol>,
            li: ({ children }) => <li className="typography-body my-1">{children}</li>,
            blockquote: ({ children }) => (
              <blockquote className="typography-body border-l-4 border-neutral-300 pl-4">{children}</blockquote>
            ),
          }}
        >
          {body || ''}
        </ReactMarkdown>
      </div>
      {!!attachmentsCount && (
        <>
          <hr />
          <div className="flex flex-col gap-4 px-4">
            <p className="">Attachments ({attachmentsCount})</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {attachments.map(({ contentUrl, fileName }, index) => (
                <a
                  key={index}
                  className="typography-body-l hover:text-secondary-600 inline-flex flex-row items-center gap-2 rounded-lg border border-neutral-300 p-2 font-medium text-neutral-800"
                  href={contentUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  <FileIcon aria-hidden="true" className="text-secondary-700 mx-1 shrink-0" />
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap">{fileName}</span>
                </a>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default MemberTicketCommentView
